import buffer from '@turf/buffer'
import { encode as htmlEncode } from 'html-entities'
import OLScaleLine from 'ol/control/ScaleLine'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import { DEVICE_PIXEL_RATIO } from 'ol/has'
import DragZoom from 'ol/interaction/DragZoom'
import {
  METERS_PER_UNIT,
  transform as OLProjTransform,
  getPointResolution
} from 'ol/proj'
import { getDistance } from 'ol/sphere'

import type { FeatureCollection } from 'geojson'
import type { Coordinate as OLCoordinate } from 'ol/coordinate'
import type OLFeature from 'ol/Feature'
import type { FeatureLike as OLFeatureLike } from 'ol/Feature'
import type OLGeometry from 'ol/geom/Geometry'
import type OLLayer from 'ol/layer/Layer'
import type Map from 'ol/Map'
import type { Pixel as OLPixel } from 'ol/pixel'
import type { ProjectionLike as OLProjectionLike } from 'ol/proj'
import type OLProjection from 'ol/proj/Projection'

import { setDirectionsLocationFromCoords } from 'api/actions'
import loadAndActivateVectorLayer from 'api/loadAndActivateVectorLayer'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import ApplyTransformers from 'config/ApplyTransformers'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { getDataFromURL } from 'drawer/tabs/info/download/Download'
import PoziDrawerTabInfo from 'drawer/tabs/info/info'
import { ShowWhatsHere, WHATS_HERE_LAYER_TITLEID } from 'drawer/tabs/info/WhatsHereUtils'
import { SET_LAYER_LOAD_FAILED, setLayerLoadDone, setLayerLoadFailed, setLayerLoadInProgress } from 'drawer/tabs/layers/actions'
import SelectLayer from 'drawer/tabs/layers/utils/SelectLayer'
import { MEASUREMENT_LAYER_TITLEID } from 'drawer/tabs/tools/MeasurementTool'
import { resetCurrentTarget } from 'map/actions'
import PoziMap from 'map/map'
import selectVectorFeature, { selectVectorFeatures } from 'map/SelectVectorFeature'
import PoziZoom from 'map/zoom'

import PoziNotifications from './notifications'

export const AutoSelectFeatureFromURL = async () => {
  const featuresFromState = Model.getStateArray('feature', null)

  console.debug('AutoSelectFeatureFromURL - featuresFromUrlState', featuresFromState)

  if (!featuresFromState || featuresFromState.length === 0) {
    return
  }

  const featureGeometries: OLGeometry[] = []

  // Get 'autozoom' from URL (it can be a string (e.g. '17' or boolean)
  const autoZoom: string | boolean = Model.getState('autozoom', false)

  // Remove 'autozoom' from state: it should only be called once, once the app is ready
  Model.unset('app.state.autozoom')

  // fullField is something like 'assets.asset_id'
  const fullField = PoziUtils.getNameOfFirstKey(featuresFromState[0]) || ''
  const [titleid, featureIdField] = fullField.split('.')
  // const idValue = featuresFromState[0][fullField]

  // Get all idValues for the first found layer
  const idValues = Model.getFeatureValuesFromState(fullField)

  console.debug('AutoSelectFeatureFromURL - state - titleid / featureIdField / idValues', titleid, featureIdField, idValues)

  if (!titleid) {
    return
  }

  const layerConf = PoziUtils.getLayerFromTitleID(titleid)

  if (!layerConf) { // layer not found
    PoziNotifications().warning(`Layer ${titleid} cannot be found, therefore feature(s) be shown`)

    return
  }

  const { attributes, serverType, showInLayerControl, title } = layerConf

  // Check if provided ID field exists in layer, but only if the layerConf has attributes defined
  const layerConfHasAttributes = attributes?.length > 0
  if (featureIdField && layerConfHasAttributes) {
    const isAttributeInLayer = PoziUtils.isAttributeInLayer(layerConf, featureIdField)

    if (!isAttributeInLayer) {
      console.log('AutoSelectFeatureFromURL - layer does not include the field', titleid, featureIdField, layerConf)

      PoziNotifications().warning(`The ${title} layer does not include a field called "${featureIdField}".`)

      return
    }
  }

  const isValidVectorLayer = layerIsValidVectorLayer(layerConf)

  if (!isValidVectorLayer) {
    console.log('AutoSelectFeatureFromURL - layer not a valid vector layer', titleid)

    PoziNotifications().warning(`The ${title} layer is not configured for link-ins.`)

    return
  }

  const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

  if (!olLayer) {
    console.log('AutoSelectFeatureFromURL - olLayer not found', titleid)

    PoziNotifications().warning(`Layer ${titleid} cannot be found in map layers, therefore no features can be shown`)

    return
  }

  let olFeatures: OLFeature[] = []

  try {
    // Show placeholder in details panel whilst fetching results
    PoziDrawerTabInfo().loadingMode()

    // Find the relevant feature either using a field value for a vector layer available in the layer control (i.e. load
    // the whole dataset), or a layer that is not available in the layer control (and construct a WFS query that will only
    // retrieve the features that match the id values).
    if (showInLayerControl) {
      // Load and activate layer irrespective of layer's scale dependency
      await loadAndActivateVectorLayer(layerConf)

      // Find the features in the layer that match the values. We also pass the optionally provided id field.
      olFeatures = PoziUtils.findFeaturesByFieldValues(titleid, idValues, featureIdField) || []

      // olFeature = PoziUtils.selectVectorFeatureFromIDTextAndLayerConfig(idValue, titleid, extraOptions)
      console.debug('AutoSelectFeatureFromURL - vector feature', titleid, olFeatures)
    } else {
      console.debug('AutoSelectFeatureFromURL - WFS query', titleid, featureIdField, idValues, layerConf)

      const getSearchResults = async () => {
        const typeName = PoziUtils.getWfsTypeNameFromTitle(title)
        const filter = createOgcFilter(featureIdField, idValues.join(','))

        if (!typeName || !filter) {
          return
        }

        const extraOptions = {
          SERVICE: 'WFS',
          VERSION: '1.1.0',
          REQUEST: 'GetFeature',
          SRSNAME: 'EPSG:4326',
          OUTPUTFORMAT: 'application/json',
          TYPENAME: typeName,
          FILTER: filter
        }

        const url = PoziUtils.getLayerUrl(layerConf, extraOptions)
        // console.debug('AutoSelectFeatureFromURL - WFS query - url', url)

        if (!url) {
          return
        }

        // The child panel code relies on the loading status of the layer in order for it to show
        // the child data. The 'loading done' status is important.
        window.store.dispatch(setLayerLoadInProgress(titleid))

        try {
          let geojson = (await getDataFromURL(url, serverType))?.data

          // If any transformers are defined, apply them. This is used for reordering the fields
          // when data has been returned from QGIS WFS for example.
          const hasTransformers = layerConf.config?.transformer
          if (hasTransformers) {
            geojson = ApplyTransformers(layerConf, geojson)
          }

          // console.debug('AutoSelectFeatureFromURL - WFS query - geojson', geojson)

          window.store.dispatch(setLayerLoadDone(titleid))

          return geojson
        } catch (error) {
          console.warn('AutoSelectFeatureFromURL - WFS query - error', error)

          window.store.dispatch(setLayerLoadFailed(titleid))
        }
      }

      const searchResults = await getSearchResults()
      if (!searchResults) {
        return
      }

      olFeatures = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(searchResults) || []
    }

    // Handle case where no features were found
    if (!olFeatures?.length) {
      PoziNotifications().warning(`Feature(s) ${idValues.join(', ')} not found in ${layerConf.title} layer`)

      // End loading mode and show details panel
      PoziDrawerTabInfo().loadingModeEnd()

      return
    }

    if (olFeatures.length > 1) {
      selectVectorFeatures(titleid, olFeatures)
    } else {
      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

      selectVectorFeature(olFeatures[0], olLayer)
    }
  } catch (err: any) {
    console.log('AutoSelectFeatureFromURL - unexpected error', err.message, err)

    PoziNotifications().warning(`Unexpected error when trying to select feature from layer ${layerConf.title}: ${err.message}`)

    // End loading mode and show details panel
    PoziDrawerTabInfo().loadingModeEnd()

    return
  }
  // }

  // Extract the geometries from the features
  for (const olFeature of olFeatures) {
    const featureGeometry = olFeature.getGeometry?.()
    if (featureGeometry) {
      featureGeometries.push(featureGeometry) // olFeature.getGeometry?.()
    }
  }

  // Handle case where no geometry is found
  if (featureGeometries.length === 0) {
    console.log('AutoSelectFeatureFromURL - no geometry found')

    PoziNotifications().warning(`No features in layer ${layerConf.title} with value(s) ${idValues.join(', ')} in field '${featureIdField}' have a geometry and therefore cannot be shown.`)

    // End loading mode and show details panel
    PoziDrawerTabInfo().loadingModeEnd()

    return
  }

  // If autozoom isn't set or set to false, don't pan/zoom to the feature
  if (!autoZoom) {
    // End loading mode and show results in details panel
    PoziDrawerTabInfo().loadingModeEnd()

    return
  }

  // If autozoom is set to true, set zoom to null, wich will zoom to the feature
  const shouldAutoZoom = typeof autoZoom === 'boolean' && autoZoom
  const zoom = shouldAutoZoom ? null : Math.round(parseFloat(autoZoom))

  console.debug('AutoSelectFeatureFromURL - zoom', zoom)

  PoziZoom().zoomToFeatures(olFeatures, { olLayer, zoom })

  PoziDrawerTabInfo().loadingModeEnd()
}

export const getMap = () => {
  const map: Map = Model.get('app.map.olMap', null)

  // console.debug('getMap', map)

  return map
}

/**
 *
 * Get the great circle distance (in meters) between two pixels
 */
export const getDistanceBetweenPixels = (pixelFrom: OLPixel, pixelTo: OLPixel) => {
  console.debug('getDistanceBetweenPixels', pixelFrom, pixelTo)

  const map = getMap()

  if (!map) {
    console.log('getDistanceBetweenPixels - map not available yet')
    return
  }

  const view = map.getView()
  const mapProjection = view.getProjection()
  const geographicProjection: OLProjectionLike = 'EPSG:7844' // Like EPSG:4326 but GDA2020 style

  const coordsFrom = map.getCoordinateFromPixel(pixelFrom) // in map projection
  const coordsFromGeographic = OLProjTransform(coordsFrom, mapProjection, geographicProjection)

  const coordsTo = map.getCoordinateFromPixel(pixelTo)
  const coordsToGeographic = OLProjTransform(coordsTo, mapProjection, geographicProjection)

  const distanceInMetres = getDistance(coordsFromGeographic, coordsToGeographic)

  // console.debug('getDistanceBetweenPixels - distance', distanceInMetres)

  return distanceInMetres
}

/**
 *
 * Get the great circle distance (in meters) from a pixel distance (like tolerance) using the current
 * map view (zoom, center, etc) and projection
 *
 */
// Note: this function is not currently in use
export const getDistanceFromPixelDistance = (pixelDistance: number) => {
  console.debug('getDistanceFromPixelDistance', pixelDistance)

  const map = getMap()

  if (!map) {
    console.log('getDistanceFromPixelDistance - map not available yet')
    return
  }

  const view = map.getView()
  const viewCenter = view.getCenter() as OLCoordinate

  console.warn('getDistanceFromPixelDistance - viewCenter', viewCenter)

  const pixel = map.getPixelFromCoordinate(viewCenter)

  // We shift right by pixelDistance
  const shiftedPixel = [pixel[0] + pixelDistance, pixel[1]]

  const distance = getDistanceBetweenPixels(pixel, shiftedPixel)

  console.warn('getDistanceFromPixelDistance - distance', distance)

  return distance
}

export const layerIsValidVectorLayer = (layerConf: ConfigLayerOptions) => {
  const { groupid, preventClick, queryable = true, showInLayerControl, titleid, type } = layerConf || {}

  const isValid = type.toLowerCase() === 'geojson' && // only vector layers
    groupid !== 'pozigeneral' && // no internal layers (like annotation and highlight)
    !preventClick && // no layers the block a mouse click (like LGA boundaries)
    queryable // only queryable

  // console.debug('layerIsValidVectorLayer - isValid?', titleid, isValid)

  return isValid
}

/**
 * Apply a pixel buffer to a feature collection
 *
 * This can be usefor for WMS GFI requests through QGIS Server using FILTER_GEOM when the feature collection
 * contains points or lines and the layer to query is one dimensional
 *
 */
// Note: this function is not currently in use
export const applyPixelBufferToFeatureCollection = (featureCollection: FeatureCollection, pixelBuffer: number) => {
  console.debug('applyPixelBufferToFeatureCollection', featureCollection, pixelBuffer)

  const meterTolerance = getDistanceFromPixelDistance(pixelBuffer)

  console.debug('applyPixelBufferToFeatureCollection - meter tolerance', meterTolerance)

  const geojsonParser = new OLFormatGeoJSON()

  const olFeatures = geojsonParser.readFeatures(featureCollection)

  console.debug('applyPixelBufferToFeatureCollection - olFeatures', olFeatures)

  const geojsonGeographic = geojsonParser.writeFeaturesObject(olFeatures, {
    dataProjection: 'EPSG:7844', // Like EPSG:4326 but GDA2020 style
    featureProjection: 'EPSG:3857'
  })

  console.debug('applyPixelBufferToFeatureCollection - geojsonGeographic', geojsonGeographic)

  const bufferedFeatureCollection = buffer(geojsonGeographic, meterTolerance, {
    units: 'meters'
  })

  console.debug('applyPixelBufferToFeatureCollection - bufferedFeatureCollection', bufferedFeatureCollection)

  return bufferedFeatureCollection
}

/**
 * Return a given scale as a human friendly string
 *
 * From: 'ol/control/ScaleLine'
 *
 */
export const getScaleStr = (scale: number) => {
  const scaleStr = scale < 1
    ? Math.round(1 / scale).toLocaleString() + ' : 1'
    : '1 : ' + Math.round(scale).toLocaleString()

  // console.debug('getScaleStr', scale, scaleStr)

  return scaleStr
}

export const getDpi = () => {
  return DEVICE_PIXEL_RATIO
}

export const getFeatureInfoAtPixel = (pixel: OLPixel, hitTolerance: number = 0) => {
  // console.debug('map - getFeatureAtPixel', pixel)

  const map = getMap()

  if (!map) {
    return
  }

  type FeatureInfo = {
    olFeature: OLFeatureLike
    olLayer: OLLayer
  }

  interface FeatureInfosPerType {
    point: FeatureInfo[]
    line: FeatureInfo[]
    polygon: FeatureInfo[]
    geometrycollection: FeatureInfo[]
    nogeometry: FeatureInfo[]
  }

  // We will sort the features by type: points on top, then lines and then polygons
  const featureInfosPerType: FeatureInfosPerType = {
    point: [],
    line: [],
    polygon: [],
    geometrycollection: [],
    nogeometry: []
  }

  // Layers to ignore
  const excludeLayers = [Model.ANNOTATION_LAYER_TITLEID, WHATS_HERE_LAYER_TITLEID]

  map.forEachFeatureAtPixel(pixel, (olFeature, olLayer) => {
    // Sometimes a feature is not attached to a layer (e.g. in the drawing tools).
    // In that case, we will ignore it.
    if (!olFeature || !olLayer) {
      return
    }

    const layerProperties = olLayer.getProperties()
    const { titleid } = layerProperties

    // Special case for Pozi measurement layer
    if (titleid === MEASUREMENT_LAYER_TITLEID) {
      const featureInfo: FeatureInfo = {
        olFeature,
        olLayer
      }
      featureInfosPerType.line.push(featureInfo)
      return
    }

    const layerConf = PoziUtils.getLayerFromOLLayer(olLayer)
    const queryable = layerConf?.queryable ?? true

    if (!layerConf || !queryable) {
      return
    }

    // Do not consider layers that are excluded
    if (excludeLayers.includes(titleid)) {
      return
    }

    const featureInfo: FeatureInfo = {
      olFeature,
      olLayer
    }

    const geomType = olFeature.getGeometry?.()?.getType?.()?.toLowerCase() || ''
    const geomTypeSimple = PoziUtils.getGeomTypeSimple(geomType)

    if (featureInfosPerType[geomTypeSimple]) {
      featureInfosPerType[geomTypeSimple].push(featureInfo)
    } else {
      console.log('Invalid geomTypeSimple', layerConf.titleid, geomTypeSimple, geomType)
    }
  }, {
    hitTolerance
  })

  const featureInfos = [
    ...featureInfosPerType.point,
    ...featureInfosPerType.line,
    ...featureInfosPerType.polygon,
    ...featureInfosPerType.geometrycollection,
    ...featureInfosPerType.nogeometry
  ]

  // console.debug('map - getFeatureInfoAtPixel - featureInfos', pixel, featureInfos)

  return featureInfos
}

// From: https://gis.stackexchange.com/a/158518
// Default to DPI of 72 due to react-pdf having that DPI
export const getResolutionForScale = (scale: number, dpi = 72) => {
  // var dpi = 25.4 / 0.28
  const mpu = METERS_PER_UNIT.m
  const inchesPerMeter = 39.37 // (100 / 2.54)
  return scale / (mpu * inchesPerMeter * dpi)
}

// Note: this function is not currently in use
export const setMapScale = (scale: number, dpi = 25.4 / 0.28) => {
  const map = getMap()

  if (!map) {
    console.log('setMapScale - map not available yet')
    return
  }

  const view = map.getView()

  const resolution = getResolutionForScale(scale, dpi)

  // console.debug('setMapScale', scale, dpi, resolution)

  // Allows for any resolution
  view.setConstrainResolution(false)

  view.setResolution(resolution)
}

/**
 * @terrestris/ol-util version of getResolutionForScale
 *
 * They appear to calculate the scale differently from the way OL's scalebar does it.
 *
 * Calculates the appropriate map resolution for a given scale in the given
 * units.
 *
 * See: https://gis.stackexchange.com/questions/158435/
 * how-to-get-current-scale-in-openlayers-3
 *
 * Code adapted from: https://github.com/terrestris/ol-util/blob/v8.1.0/src/MapUtil/MapUtil.ts#L48
 * Not importing the whole file as the import size is way too big
 *
 * TODO: investigate why the scales are different!
 *
 * @method
 * @param {number|string} scale The input scale to calculate the appropriate
 *                       resolution for.
 * @return {number} The calculated resolution.
 */
export const getResolutionForScaleTerrestris = (scale: number | string): number => {
  const dpi = 25.4 / 0.28
  const mpu = METERS_PER_UNIT.m
  const inchesPerMeter = 39.37

  return (typeof scale === 'string' ? parseFloat(scale) : scale) / (mpu * inchesPerMeter * dpi)
}

/**
 * Returns the appropriate scale for the given resolution and units.
 *
 * From: 'ol/control/ScaleLine'
 *
 */
// Note: this function is not currently in use
export const getScaleForResolution = (options: Record<string, any> = {}) => {
  const map = getMap()

  if (!map) {
    console.log('getScaleForResolution - map not available yet')
    return
  }

  const view = map.getView()

  const projection: OLProjection = options.projection || view.getProjection()
  const resolution: number = options.resolution || view.getResolution()
  const center: OLCoordinate = options.center || view.getCenter()

  // console.debug('getScaleForResolution - projection', projection)
  // console.debug('getScaleForResolution - resolution', resolution)
  // console.debug('getScaleForResolution - center', center)

  const pointResolution = getPointResolution(
    projection,
    resolution,
    center,
    'm'
  )

  // console.debug('getScaleForResolution - pointResolution', pointResolution)

  const DEFAULT_DPI = 25.4 / 0.28
  const dpi = options.dpi || DEFAULT_DPI
  const inchesPerMeter = 1000 / 25.4

  const scale = Math.round(pointResolution * inchesPerMeter * dpi)

  // console.debug('getScaleForResolution - scale', scale, `${pointResolution} * ${inchesPerMeter} * ${dpi}`)

  return scale
}

/**
 *
 * @terrestris/ol-util version of getScaleForResolution
 *
 * They appear to calculate the scale differently from the way OL's scalebar does it.
 *
 * Code adapted from: https://github.com/terrestris/ol-util/blob/v8.1.0/src/MapUtil/MapUtil.ts#L69
 * Not importing the whole file as the import size is way too big
 *
 * TODO: investigate why the scales are different!
 *
 * Returns the appropriate scale for the given resolution and units.
 *
 * @method
 * @param {number|string} resolution The resolution to calculate the scale for.
 * @return {number} The appropriate scale.
 */
// Note: this function is not currently in use
export const getScaleForResolutionTerrestris = (resolution: number | string): number => {
  const dpi = 25.4 / 0.28
  const mpu = METERS_PER_UNIT.m
  const inchesPerMeter = 39.37

  return (typeof resolution === 'string' ? parseFloat(resolution) : resolution) * mpu * inchesPerMeter * dpi
}

export const setScaleLineDpi = (dpi: number | undefined) => {
  const map = getMap()

  const controls = map.getControls().getArray()

  const scaleLine = controls.find((control: any) => control instanceof OLScaleLine) as OLScaleLine

  if (!scaleLine) {
    console.log('getScaleLineDpi - control not found', controls)
    return
  }

  // console.debug('getScaleLineDpi - control', scaleLine, dpi)

  scaleLine.setDpi(dpi)
}

// Note: this function is not currently in use
export const getDragZoomUnconstrained = () => {
  const map = getMap()

  if (!map) {
    console.log('getDragZoomUnconstrained - map not initialised yet.')
    return
  }

  const mapView = map.getView()

  const dragZoomUnconstrained = !mapView.getConstrainResolution()

  // console.debug('getDragZoomUnconstrained', dragZoomUnconstrained)

  return dragZoomUnconstrained
}

export const getVisibleLayers = () => {
  const state = window.store.getState()

  const layerConfs = []

  const stateLayers: Record<string, ConfigLayerOptions> = state?.layers

  if (!stateLayers || !Object.keys(stateLayers).length) {
    console.log('getVisibleLayers: No layers found in redux store!')
    return null
  }

  const bLocalDataSourceDetected = Model.get('app.LocalDataSourceDetected', false)

  // console.debug('getVisibleLayers - looping through layers from redux state', state.layers)

  for (const layerConf of Object.values(stateLayers)) {
    const { groupid, localDataSource, parent: parentTitle, selected, titleid: childTitleId, type } = layerConf
    // console.debug('getVisibleLayers', childTitleId, parentTitle, selected, layerConf)

    if (!selected) {
      // console.debug('getVisibleLayers - layer not selected. Skipping.', childTitleId, parentTitle)
      continue
    }

    const bLocalDataSource = localDataSource ?? false
    if (bLocalDataSource && !bLocalDataSourceDetected) {
      console.debug('getVisibleLayers - local datasource required but not detected. Skipping.', childTitleId)
      continue
    }

    // console.debug('getVisibleLayers - visible layer found!', childTitleId, groupid)

    layerConfs.push(layerConf)
  }

  return layerConfs
}

// Given a field name and value(s), create a OGC Filter statement that can be appended to URL for a WFS service
export const createOgcFilter = (fieldName: string | null, values: string | null) => {
  if (!fieldName || !values) {
    return
  }

  const valuesArray = values.split(',').map((value) => value.trim())

  const propertyIsEqualToElements = []
  for (const value of valuesArray) {
    propertyIsEqualToElements.push(
      '<PropertyIsEqualTo>' +
      '<PropertyName>' + htmlEncode(fieldName) + '</PropertyName>' +
      '<Literal>' + htmlEncode(value) + '</Literal>' +
      '</PropertyIsEqualTo>'
    )
  }

  return (
    '<Filter>' +
    '<Or>' +
    propertyIsEqualToElements.join('') +
    '</Or>' +
    '</Filter>'
  )
}

// This allows for the drag/zoom functionality to give more control over zooming
// the map to the desired scale/resolution. This was a request from a client to
// allow them to have more freedom to determine what is visible in the print output.
export const setDragZoomUnconstrained = () => {
  const map = getMap()

  if (!map) {
    console.log('setDragZoomUnconstrained - map not initialised yet.')
    return
  }

  const mapInteractions = map.getInteractions().getArray()
  // console.debug('setDragZoomUnconstrained - interactions', mapInteractions)

  const dragZoom = mapInteractions.find(interaction => interaction instanceof DragZoom) as DragZoom
  // console.debug('setDragZoomUnconstrained - dragZoom', dragZoom)

  if (!dragZoom) {
    console.log('setDragZoomUnconstrained - dragZoom not found.')
    return
  }

  const mapView = map.getView()

  // This function will re-enable the fixed zoom levels constrained
  const onResolutionChange = () => {
    // console.debug('setDragZoomUnconstrained - resolution changed')

    // Add a delay to make the UX slightly better
    window.setTimeout(() => {
      mapView.setConstrainResolution(true)
    }, 750)
  }

  // When starting the dragzoom functionality, remove the constraint to
  // have fixed zoom levels
  dragZoom.on('boxstart', () => {
    // console.debug('setDragZoomUnconstrained- dragzoom draw start')

    // Unregister function that resets the constraint for limiting
    // to fixed zoom levels as we are drag/zooming again!
    mapView.un('change:resolution', onResolutionChange)

    mapView.setConstrainResolution(false)
  })

  // Wait untill map has zoom/panned as a result of the drag/zoom and
  // then re-enable the "zoom to fixed levels" constraint when the next
  // zoom activity occurs (by listening to the changes in resolution)
  dragZoom.on('boxend', () => {
    // console.debug('setDragZoomUnconstrained - dragzoom draw end')

    map.once('moveend', () => {
      mapView.once('change:resolution', onResolutionChange)
    })
  })
}

export const isLoading = () => {
  const map = getMap()

  if (!map) {
    console.log('isLoading - map not initialised')

    return false
  }

  const reduxLayerConf = window.store.getState()

  const layerStatesArray = map.getLayerGroup().getLayerStatesArray()

  const layersLoading = layerStatesArray
    .filter(state => state.layer.getSource()?.loading)
    .filter(state => reduxLayerConf.layers[state.layer.get('titleid')]?.loadstatus !== SET_LAYER_LOAD_FAILED)

  // console.debug('isLoading - layersLoading', layersLoading)

  const hasLayersLoading = layersLoading.length > 0

  console.debug('MapUtils - isLoading', hasLayersLoading)

  return hasLayersLoading
}

export const waitForMapLoad = (force = false) => new Promise(resolve => {
  const mapIsLoading = isLoading()

  // console.debug('waitForMapLoad - map is loading. Forced wait?', mapIsLoading, force)

  if (!mapIsLoading && !force) {
    // console.debug('waitForMapLoad - map was not loading. No need to wait.')

    return resolve(true)
  }

  const map = getMap()
  if (!map) {
    console.log('waitForMapLoad - map not initialised yet.')
    return resolve(false)
  }

  map.once('loadend', () => {
    console.debug('waitForMapLoad - map loaded')

    return resolve(true)
  })
})

export const activateLayersVisibleOnLoad = () => {
  const layerConfs = window.appObject.siteConfig.layers as ConfigLayerOptions[]
  const visibleConfs = layerConfs.filter(l => l.selected)

  console.debug('activateLayersVisibleOnLoad - layers', visibleConfs.map(l => l.titleid))

  for (const layerConf of visibleConfs) {
    const { titleid } = layerConf

    const layerIsSelected = PoziUtils.isLayerSelected(layerConf, true)
    if (layerIsSelected) {
      // console.debug('activateLayersVisibleOnLoad - layer is selected', titleid)

      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)
      if (!olLayer) {
        // console.debug('activateLayersVisibleOnLoad - OL layer not found', titleid)

        continue
      }

      SelectLayer(titleid)
    }
  }
}

export const runDirections = function (coords4326: OLCoordinate) {
  console.debug('Run directions ...', coords4326)

  window.store.dispatch(setDirectionsLocationFromCoords(coords4326))
}

export const runWhatsHere = async (coordinate: OLCoordinate, doLayersIntersect = false) => {
  console.debug('Run whats here ...', coordinate)

  // Show placeholder in details panel whilst fetching results
  PoziDrawerTabInfo().loadingMode()

  // doLayersIntersect=true will cause intersect layers to be shown in the whats here panel
  // See the code for 'isRightClick' in ChildPanels.jsx
  // TODO: rename this setting
  if (doLayersIntersect) {
    Model.set('app.event.map.click.right', true) // this will be used in the whats here panel
  }

  // The following give the UI the chance to update before runWhatsHere
  // blocks the thread.... (yes, this is a hack)
  await PoziUtils.sleep(10)

  // Additional processing in PoziMap().onChangePosition if no vector feature was clicked
  const app = PoziUtils.getApp()

  app.selectMarker.setPosition(coordinate) // Triggers PoziMap().onChangePosition

  // Hide marker if CTRL key is pressed
  const ctrlKey = Model.get('app.map.click.controlkey', false)
  if (ctrlKey) {
    PoziMap().removeMarker()
  } else {
    PoziMap().showMarker()
  }
}

// This gets called when there are multiple features (to be) selected
export const runWhatsThese = async (featureInfos: featureInfoType[], currentTarget: any) => {
  const { titleid } = currentTarget

  const featureInfosFiltered = featureInfos.filter(featureInfo => featureInfo.olLayer?.get('titleid') === titleid)
  const olFeaturesCurrentTarget: OLFeature[] = currentTarget.olFeatures || []

  const olFeaturesFromClick = featureInfosFiltered.map(featureInfo => featureInfo.olFeature)

  // console.debug('map - runWhatsThese - olFeaturesFromClick', olFeaturesFromClick)
  // console.debug('map - runWhatsThese - olFeaturesCurrentTarget', olFeaturesCurrentTarget)

  // Use 'Set' to make sure that there are no duplicate features
  const olFeatures = [...new Set([...olFeaturesCurrentTarget, ...olFeaturesFromClick])]

  // console.debug('map - runWhatsThese - olFeatures', olFeatures)

  // Due to timing issues, we need to reset the currentTarget and wait for the next tick before calling selectVectorFeatures
  window.store.dispatch(resetCurrentTarget())
  await PoziUtils.sleep(0)

  // Handle case where CTRL-click on the same feature would result in only 1 feature being selected.
  // In any other situation, we'll call selectVectorFetaures
  if (olFeatures.length === 1) {
    const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)
    selectVectorFeature(olFeatures[0], olLayer)
  } else {
    selectVectorFeatures(titleid, olFeatures)
  }

  // Reset the control key
  Model.set('app.map.click.controlkey', false)
}

export const runWhatsThis = async (featureInfo: featureInfoType) => {
  console.debug('Run whats this ...', featureInfo)

  // Due to timing issues, we need to reset the currentTarget and wait for the next tick before calling selectVectorFeature
  window.store.dispatch(resetCurrentTarget())
  await PoziUtils.sleep(0) // needed for the resetCurrentTarget to take effect

  selectVectorFeature(featureInfo.olFeature, featureInfo.olLayer)

  // Reset the control key
  Model.set('app.map.click.controlkey', false)
}

export const runWhatIntersectsWithThis = async (featureInfo: featureInfoType) => {
  console.debug('runWhatIntersectsWithThis ...', featureInfo)

  PoziMap().removeMarker()

  // Due to timing issues, we need to reset the currentTarget and wait for the next tick before calling showWhatsHere
  window.store.dispatch(resetCurrentTarget())
  await PoziUtils.sleep(0)

  // TODO: make this dependent on the projection of the layer
  // Is it necessary to use the projection of the layer, or are features always returned in EPSG:4326??
  const options = {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857'
  }

  // Use featureCollection if provided, else fall back to olFeature
  let featureCollection = featureInfo.featureCollection
  if (!featureCollection && featureInfo.olFeature) {
    featureCollection = PoziUtils.convertOLFeatureArrToTurfFeature([featureInfo.olFeature], options)
  }

  console.debug('runWhatIntersectsWithThis - fc', featureCollection)

  ShowWhatsHere(featureCollection)
}

export interface featureInfoType {
  featureCollection?: FeatureCollection | null,
  olFeature?: OLFeature,
  olLayer?: OLLayer
}
